import { t } from "@redotech/redo-i18n";
import { AdjustmentType, CompensationMethod, ReturnTypeEnum } from "../return";
import { ReturnStatus } from "../return-status";

export const adjustmentTypeToLabel: Record<AdjustmentType, string> = {
  [AdjustmentType.CHOOSE_ITEMS_FLOW]: "Price adjustment",
  [AdjustmentType.MERCHANT_MANUAL]: "Merchant adjustment",
  [AdjustmentType.CUSTOMER_LABEL_UPCHARGE_FEE]: "Customer label upcharge fee",
  [AdjustmentType.DYNAMIC_BUNDLE_ADJUSTMENT]: "Dynamic bundle adjustment",
  [AdjustmentType.FINALIZE_FLOW]: "Finalize flow adjustment",
  [AdjustmentType.USE_IT_OR_LOSE_IT]: "Use-it-or-lose-it adjustment",
};

export function getAdjustmentTypeLabel(type: AdjustmentType): string {
  return adjustmentTypeToLabel[type] ?? type;
}

export function translatedReturnType(type: ReturnTypeEnum): string {
  switch (type) {
    case ReturnTypeEnum.RETURN:
      return t("return");
    case ReturnTypeEnum.CLAIM:
      return t("claim");
    case ReturnTypeEnum.WARRANTY:
      return t("warranty claim");
    case ReturnTypeEnum.RECYCLE:
      return t("recycling request");
  }
}

/**
 * Get header text for a return based on its status and compensation methods
 */
export function getReturnHeaderText(params: {
  status: string;
  returnType: CompensationMethod[];
  type: ReturnTypeEnum;
}): string {
  const { status, returnType, type } = params;

  const isRejected =
    status === ReturnStatus.REJECTED || status === ReturnStatus.DELETED;

  const displayType = translatedReturnType(type);

  if (status === ReturnStatus.COMPLETE) {
    if (returnType.includes(CompensationMethod.EXCHANGE)) {
      return t("Exchange complete");
    }
    return t("{{type}} complete", { type: displayType });
  }

  if (!isRejected) {
    return t("{{type}} in progress", { type: displayType });
  }

  return t("{{type}} rejected", { type: displayType });
}

/**
 * Custom status subtitles that can be configured per team
 */
export interface CustomStatusSubtitles {
  returnStatusSubtitle?: string;
  exchangeStatusSubtitle?: string;
  storeCreditStatusSubtitle?: string;
}

/**
 * Get a detailed status description for a return, including date and compensation methods
 */
export function getReturnStatusDescription(params: {
  createdAt: Date | string;
  status: string;
  returnType: CompensationMethod[];
  dateFormatter: (date: Date) => string;
  type: ReturnTypeEnum;
  customSubtitles?: CustomStatusSubtitles;
}): string {
  const {
    createdAt,
    status,
    returnType,
    dateFormatter,
    type,
    customSubtitles,
  } = params;

  const date = dateFormatter(
    typeof createdAt === "string" ? new Date(createdAt) : createdAt,
  );

  const hasCustomRefund = !!customSubtitles?.returnStatusSubtitle;
  const hasCustomExchange = !!customSubtitles?.exchangeStatusSubtitle;
  const hasCustomStoreCredit = !!customSubtitles?.storeCreditStatusSubtitle;

  const compensationMethodToAction: Record<CompensationMethod, string> = {
    [CompensationMethod.REFUND]:
      customSubtitles?.returnStatusSubtitle || t("a refund has been issued"),
    [CompensationMethod.EXCHANGE]:
      customSubtitles?.exchangeStatusSubtitle ||
      t("an exchange has been processed"),
    [CompensationMethod.STORE_CREDIT]:
      customSubtitles?.storeCreditStatusSubtitle ||
      t("store credit has been issued"),
  };

  const actions = returnType
    .map((rt) => compensationMethodToAction[rt])
    .filter(Boolean);

  // Only use t() for list joining when none of the actions are custom merchant text
  const hasAnyCustom = returnType.some(
    (rt) =>
      (rt === CompensationMethod.REFUND && hasCustomRefund) ||
      (rt === CompensationMethod.EXCHANGE && hasCustomExchange) ||
      (rt === CompensationMethod.STORE_CREDIT && hasCustomStoreCredit),
  );

  let action = "";
  switch (actions.length) {
    case 0: {
      action = t("it has been processed");

      break;
    }
    case 1: {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      action = actions[0];

      break;
    }
    case 2: {
      if (hasAnyCustom) {
        action = `${actions[0]} and ${actions[1]}`;
      } else {
        action = t("{{first}} and {{second}}", {
          first: actions[0],
          second: actions[1],
        });
      }

      break;
    }
    default: {
      const lastAction = actions[actions.length - 1];
      const otherActions = actions.slice(0, -1).join(", ");
      if (hasAnyCustom) {
        action = `${otherActions}, and ${lastAction}`;
      } else {
        action = t("{{list}}, and {{last}}", {
          list: otherActions,
          last: lastAction,
        });
      }
    }
  }

  const displayType = translatedReturnType(type);

  if (status === ReturnStatus.COMPLETE) {
    return t(
      "Your {{type}} was submitted on {{date}}. It has been completed and {{action}}.",
      { type: displayType, date, action },
    );
  } else if (
    status === ReturnStatus.REJECTED ||
    status === ReturnStatus.DELETED
  ) {
    return t("Your {{type}} was submitted on {{date}}. It has been rejected.", {
      type: displayType,
      date,
    });
  } else {
    return t("Your {{type}} was submitted on {{date}}.", {
      type: displayType,
      date,
    });
  }
}
